import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Calendar, CreditCard, ClipboardList, Menu, Trophy } from 'lucide-react';

/**
 * BottomTabBar — mobile primary navigation (Design System v1, issue #50).
 *
 * Six fixed tabs: Home · Bookings · Payments · Tournaments · Ops · More.
 * - Payments and Bookings show live badge counts.
 * - The active tab reflects the current route (longest-prefix match); when the
 *   route isn't one of the four primary destinations, "More" is highlighted.
 * - "More" opens the MoreSheet (handled by the parent via onOpenMore).
 *
 * Rendered only on mobile; desktop keeps the sidebar.
 */
const TABS = [
  { key: 'home',     label: 'Home',     to: '/dashboard',                 icon: LayoutDashboard, match: ['/dashboard'] },
  { key: 'bookings', label: 'Bookings', to: '/dashboard/book-now',        icon: Calendar,        match: ['/dashboard/book-now', '/dashboard/my-bookings', '/dashboard/booking-requests', '/dashboard/waitlist', '/dashboard/booking'] },
  { key: 'payments', label: 'Payments', to: '/dashboard/payments',        icon: CreditCard,      match: ['/dashboard/payments'] },
  { key: 'tournaments', label: 'Tournament', to: '/dashboard/tournaments', icon: Trophy,          match: ['/dashboard/tournaments'] },
  { key: 'ops',      label: 'Ops',      to: '/dashboard/operations/daily', icon: ClipboardList,   match: ['/dashboard/operations'] },
];

const Badge = ({ count }) => {
  if (!count) return null;
  return (
    <span className="absolute -top-1.5 right-[22%] min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full bg-ga-danger text-white text-[10px] font-bold leading-none ring-2 ring-ga-bg-surface">
      {count > 99 ? '99+' : count}
    </span>
  );
};

export default function BottomTabBar({
  onOpenMore,
  moreOpen = false,
  pendingPaymentsCount = 0,
  pendingRequestCount = 0,
  showTournaments = false,
}) {
  const location = useLocation();
  const visibleTabs = useMemo(
    () => TABS.filter((tab) => tab.key !== 'tournaments' || showTournaments),
    [showTournaments]
  );

  const activeKey = useMemo(() => {
    const path = location.pathname;
    let best = null;
    let bestLen = -1;
    for (const tab of visibleTabs) {
      for (const m of tab.match) {
        if (path === m || path.startsWith(m + '/')) {
          if (m.length > bestLen) { bestLen = m.length; best = tab.key; }
        }
      }
    }
    return best; // null => none of the primary tabs → "More" is active
  }, [location.pathname, visibleTabs]);

  const tabClass = (isActive) =>
    `relative flex flex-1 flex-col items-center justify-center gap-0.5 py-1.5 min-w-0 transition-colors ${
      isActive ? 'text-ga-primary' : 'text-ga-text-muted'
    }`;

  const badgeFor = (key) =>
    key === 'payments' ? pendingPaymentsCount : key === 'bookings' ? pendingRequestCount : 0;

  return (
    <nav
      className="ga-bottom-tabs lg:hidden fixed bottom-0 left-0 right-0 z-40"
      aria-label="Primary"
    >
      <div className="flex items-stretch">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeKey === tab.key && !moreOpen;
          return (
            <Link
              key={tab.key}
              to={tab.to}
              className={tabClass(isActive)}
              aria-current={isActive ? 'page' : undefined}
              aria-label={tab.label}
            >
              <span className="relative">
                <Icon className="h-[22px] w-[22px]" strokeWidth={isActive ? 2.4 : 2} aria-hidden />
                <Badge count={badgeFor(tab.key)} />
              </span>
              <span className="text-[10.5px] font-medium leading-none truncate max-w-full">{tab.label}</span>
            </Link>
          );
        })}

        <button
          type="button"
          onClick={onOpenMore}
          className={tabClass(moreOpen || activeKey === null)}
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          aria-label="More"
        >
          <span className="relative">
            <Menu className="h-[22px] w-[22px]" strokeWidth={moreOpen || activeKey === null ? 2.4 : 2} aria-hidden />
          </span>
          <span className="text-[10.5px] font-medium leading-none">More</span>
        </button>
      </div>
    </nav>
  );
}
